import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  DestroyRef,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
  HlmButton,
  HlmInput,
  HlmLabel,
  HlmSpinnerImports,
  O2PasswordInputComponent,
} from "@o2-fe/ui";
import {
  AuthenticateErrorEnum,
  AuthenticateStateEnum,
  SignInEnum,
} from "../../enums/authenticate.enum";
import { CognitoSignInResult } from "../../interfaces/auth.interfaces";
import { AuthService } from "../../services/auth.service";
import { AccountDeactivatedService } from "../../../core/services/account-deactivated.service";

@Component({
  selector: "app-sign-in",
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    HlmInput,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    HlmSpinnerImports,
    O2PasswordInputComponent,
  ],
  host: {
    class: "block",
  },
  templateUrl: "./sign-in.component.html",
})
export class SignInComponent {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _router: Router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _accountDeactivated = inject(AccountDeactivatedService);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  @Output()
  protected readonly signInEvent = new EventEmitter<AuthenticateStateEnum>();

  protected readonly form = new FormGroup({
    email: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected signIn(): void {
    this.errorMessage.set(undefined);

    const { email, password } = this.form.value;

    // Only check if values are present, let Cognito handle validation
    if (!email || !password) {
      this.errorMessage.set("Inserisci email e password");
      return;
    }

    this.isLoading.set(true);
    this._authService
      .signIn(email, password)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result: CognitoSignInResult) => {
          switch (result.type) {
            case SignInEnum.NEW_PASSWORD_REQUIRED:
              this.signInEvent.emit(AuthenticateStateEnum.RESET_PASSWORD);
              break;
            case SignInEnum.SUCCESS:
              void this._router.navigate(["/planning/activities"]);
              break;
          }
          this.isLoading.set(false);
        },
        error: (error: CognitoSignInResult) => {
          const result = error.result as Error;

          if (
            result.name ===
            (AuthenticateErrorEnum.USER_NOT_CONFIRMED_EXCEPTION as string)
          ) {
            this.signInEvent.emit(AuthenticateStateEnum.CONFIRM_SIGN_UP);
          } else if (
            result.name ===
            (AuthenticateErrorEnum.PASSWORD_RESET_REQUIRED_EXCEPTION as string)
          ) {
            this.signInEvent.emit(AuthenticateStateEnum.RESET_PASSWORD);
          } else if (
            result.name ===
              (AuthenticateErrorEnum.NOT_AUTHORIZED_EXCEPTION as string) &&
            result.message?.toLowerCase().includes("disabled")
          ) {
            // Cognito account disabled: redirect to the deactivation page
            // instead of showing the raw "User is disabled" error.
            this._accountDeactivated.signaled = true;
            void this._router.navigate(["/account-deactivated"]);
          } else {
            this.errorMessage.set(result.message);
          }
          this.isLoading.set(false);
        },
      });
  }

  protected onChangePasswordClick(): void {
    this.signInEvent.emit(AuthenticateStateEnum.FORGOT_PASSWORD);
  }

  protected onSignUpClick(): void {
    this.signInEvent.emit(AuthenticateStateEnum.SIGN_UP);
  }
}
